import type { ShakaPlayer } from '~/composables/useSharedVideoPlayer';
import type { Ref } from 'vue';

import {
	useElementHover,
	useEventListener,
	useIntersectionObserver,
	useTimeoutFn
} from '@vueuse/core';
import {
	createPlayer,
	destroyPlayer
} from '~/composables/useSharedVideoPlayer';
import { getProxiedVideoUrl } from '~/composables/useVideoUrls';
import { computed } from 'vue';

/**
 * Awaits the first `canplay` or completes immediately if `signal` is aborted (ex.: unmount).
 * Always resolves; the caller must check `signal.aborted` and `shouldPlay` before `play()`.
 */
function waitUntilVideoCanPlay(
	video: HTMLVideoElement,
	signal: AbortSignal
): Promise<void> {
	if (video.readyState !== 0) return Promise.resolve();
	if (signal.aborted) return Promise.resolve();
	return new Promise(resolve => {
		const finish = () => resolve();
		video.addEventListener('canplay', finish, { once: true, signal });
		signal.addEventListener('abort', finish, { once: true });
	});
}

interface UseVideoCardPlayerOptions {
	videoUrl: Ref<string | null>;
	isActive: Ref<boolean>;
	hoverToPlay: Ref<boolean>;
	index: Ref<number>;
	playerRootMargin?: string;
	/** Fonte de hover externa. Quando omitido, usa `useElementHover(cardRef)`. */
	isHovered?: Ref<boolean>;
}

export function useVideoCardPlayer({
	videoUrl,
	isActive,
	hoverToPlay,
	index,
	playerRootMargin = '100px',
	isHovered: externalHovered
}: UseVideoCardPlayerOptions) {
	const tag = () => `[Video #${index.value}]`;

	function hasVideoSource(): boolean {
		return Boolean(videoUrl.value?.trim());
	}

	const cardRef = ref<HTMLDivElement | null>(null);
	const videoRef = ref<HTMLVideoElement | null>(null);
	const shakaPlayer = shallowRef<ShakaPlayer | null>(null);

	const isHovered = externalHovered ?? useElementHover(cardRef);
	const isVideoPlaying = ref(false);
	const isVisible = ref(false);
	const isPlayerReady = ref(false);
	const isInitializing = ref(false);
	const isAutoplayBlocked = ref(false);
	const videoProgress = ref(0);
	const isMuted = ref(true);
	let initGeneration = 0;
	let savedCurrentTime = 0;
	/** In-flight `canplay` wait; aborted on unmount or superseded by a newer `safePlay`. */
	let pendingCanPlayAbort: AbortController | null = null;

	const shouldPlay = computed(
		() => (hoverToPlay.value && isHovered.value) || isActive.value
	);
	const isLoading = computed(
		() =>
			shouldPlay.value && !isVideoPlaying.value && !isAutoplayBlocked.value
	);
	const proxiedVideoUrl = computed(() => getProxiedVideoUrl(videoUrl.value));

	function handleMuteToggle() {
		isMuted.value = !isMuted.value;
		if (videoRef.value) {
			videoRef.value.muted = isMuted.value;
		}
	}

	function seekTo(ratio: number) {
		if (videoRef.value && videoRef.value.duration) {
			videoRef.value.currentTime = ratio * videoRef.value.duration;
		}
	}

	async function safePlay(video: HTMLVideoElement) {
		if (video.readyState === 0) {
			pendingCanPlayAbort?.abort();
			const canPlayWait = new AbortController();
			pendingCanPlayAbort = canPlayWait;
			try {
				await waitUntilVideoCanPlay(video, canPlayWait.signal);
			} finally {
				if (pendingCanPlayAbort === canPlayWait) {
					pendingCanPlayAbort = null;
				}
			}
			if (canPlayWait.signal.aborted || !shouldPlay.value) return;
		}

		isAutoplayBlocked.value = false;

		try {
			await video.play();
		} catch (err) {
			if (err instanceof DOMException && err.name === 'NotAllowedError') {
				video.muted = true;
				isMuted.value = true;
				try {
					await video.play();
				} catch (retryErr) {
					if (
						retryErr instanceof DOMException &&
						retryErr.name === 'NotAllowedError'
					) {
						isAutoplayBlocked.value = true;
					}
					console.error(tag(), 'safePlay: muted retry also failed', retryErr);
				}
			} else {
				console.error(tag(), 'safePlay: play() failed', err);
			}
		}
	}

	useEventListener(videoRef, 'playing', () => {
		isVideoPlaying.value = true;
		isAutoplayBlocked.value = false;
	});

	useEventListener(videoRef, 'pause', () => {
		isVideoPlaying.value = false;
	});

	useEventListener(videoRef, 'error', () => {
		const err = videoRef.value?.error;
		console.error(tag(), 'EVENT error', {
			code: err?.code,
			message: err?.message
		});
	});

	useEventListener(videoRef, 'timeupdate', () => {
		if (videoRef.value && videoRef.value.duration) {
			videoProgress.value =
				(videoRef.value.currentTime / videoRef.value.duration) * 100;
		}
	});

	const { start: startPreload, stop: stopPreload } = useTimeoutFn(
		() => {
			if (isVisible.value && !isPlayerReady.value && hasVideoSource()) {
				initPlayer();
			}
		},
		300,
		{ immediate: false }
	);

	useIntersectionObserver(
		cardRef,
		entries => {
			const entry = entries[0];
			if (!entry) return;

			if (entry.isIntersecting) {
				isVisible.value = true;
				if (!hasVideoSource()) return;
				if (hoverToPlay.value) {
					startPreload();
				} else if (shouldPlay.value && !isPlayerReady.value) {
					cancelPlayDebounce();
					startPlayDebounce();
				}
			} else {
				isVisible.value = false;
				cancelPlayDebounce();
				stopPreload();
				cleanupPlayer();
			}
		},
		{ rootMargin: playerRootMargin }
	);

	async function initPlayer() {
		if (
			!hasVideoSource() ||
			!proxiedVideoUrl.value ||
			!videoRef.value ||
			isPlayerReady.value ||
			isInitializing.value
		)
			return;

		const initStart = performance.now();

		isInitializing.value = true;
		const generation = ++initGeneration;

		try {
			const startTime = savedCurrentTime > 0 ? savedCurrentTime : undefined;
			const url = proxiedVideoUrl.value;

			const player = await createPlayer(url, videoRef.value, {
				startTime
			});

			if (generation !== initGeneration || !videoRef.value) {
				destroyPlayer(player);
				return;
			}

			shakaPlayer.value = player;
			isPlayerReady.value = true;

			if (shouldPlay.value && videoRef.value) safePlay(videoRef.value);
		} catch (err) {
			const elapsed = Math.round(performance.now() - initStart);
			console.error(tag(), `initPlayer ERROR after ${elapsed}ms`, err);
		} finally {
			isInitializing.value = false;
		}
	}

	function cleanupPlayer() {
		initGeneration++;
		if (shakaPlayer.value) {
			if (videoRef.value) savedCurrentTime = videoRef.value.currentTime;
			destroyPlayer(shakaPlayer.value);
			shakaPlayer.value = null;
			isPlayerReady.value = false;
			isVideoPlaying.value = false;
			isAutoplayBlocked.value = false;
		}
	}

	const PLAY_DEBOUNCE_MS = 200;

	const { start: startPlayDebounce, stop: cancelPlayDebounce } = useTimeoutFn(
		() => {
			if (shouldPlay.value && !isPlayerReady.value && videoRef.value) {
				stopPreload();
				initPlayer();
			}
		},
		PLAY_DEBOUNCE_MS,
		{ immediate: false }
	);

	watch(
		shouldPlay,
		playing => {
			cancelPlayDebounce();

			if (!hasVideoSource() || !videoRef.value) return;

			if (playing) {
				if (isPlayerReady.value) {
					safePlay(videoRef.value);
				} else {
					startPlayDebounce();
				}
			} else if (isPlayerReady.value) {
				videoRef.value.pause();
			}
		},
		{ immediate: true }
	);

	onBeforeUnmount(() => {
		pendingCanPlayAbort?.abort();
		pendingCanPlayAbort = null;
		cancelPlayDebounce();
		stopPreload();
		cleanupPlayer();
	});

	return {
		cardRef,
		videoRef,
		isVideoPlaying,
		isVisible,
		isLoading,
		isInitializing,
		isAutoplayBlocked,
		shouldPlay,
		videoProgress,
		isMuted,
		handleMuteToggle,
		seekTo
	};
}
